import React, { useCallback, useEffect, useRef } from 'react';
import {
  ArrowUp,
  Mic,
  Square,
  X
} from 'lucide-react';
import { ImagePurposeAttachMenu } from '../../components/shared/ImagePurposeAttachMenu';
import { useI18n } from '../../hooks/useI18n';
import { useSpeechInput } from '../../hooks/useSpeechInput';
import { classNames } from '../../utils/classNames';
import { QuoteBar } from './QuoteBar';
import { VoiceDictationBar } from './VoiceDictationBar';
import { listImageFiles } from '../../utils/referenceImages';

const SuggestionChips = ({ items, onRemove, removeLabel }) => {
  if (!items.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1.5 px-3 pt-3">
      {items.map((label, index) => (
        <span
          key={`${label}-${index}`}
          className="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs leading-5 text-neutral-800 shadow-sm dark:bg-neutral-800 dark:text-neutral-100"
        >
          <span className="min-w-0 truncate">{label}</span>
          <button
            type="button"
            onClick={() => onRemove?.(label)}
            aria-label={removeLabel}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-700 dark:hover:text-white"
          >
            <X size={10} />
          </button>
        </span>
      ))}
    </div>
  );
};

export const ChatComposer = ({
  message,
  onMessageChange,
  selectedSuggestions = [],
  onRemoveSuggestion,
  loading = false,
  uploadingAttachment = false,
  canSend = true,
  attachments = [],
  onAttachmentSelect,
  onAttachmentRemove,
  onSubmit,
  onStop,
  quote = null,
  onClearQuote,
  voiceError = '',
  onVoiceError
}) => {
  const { t, language } = useI18n();
  const textareaRef = useRef(null);
  const dictationBaseRef = useRef('');
  const voiceAttachRef = useRef(null);
  const hasAttachment = attachments.length > 0;
  const hasSelectedSuggestions = selectedSuggestions.length > 0;
  const canSubmit = Boolean(message.trim()) || hasAttachment || hasSelectedSuggestions;

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 88), 168)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [message, resizeTextarea]);

  const speechInput = useSpeechInput({
    language: language === 'fa' ? 'fa-IR' : 'en-US',
    onResult: (transcript) => {
      if (!transcript) {
        return;
      }
      onMessageChange(`${dictationBaseRef.current}${transcript}`);
    },
    onError: (code) => {
      if (code === 'unsupported') {
        onVoiceError?.(t('chatVoiceUnsupported'));
        return;
      }
      if (code !== 'aborted' && code !== 'no-speech') {
        onVoiceError?.(t('chatVoiceError'));
      }
    }
  });

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
        return;
      }

      event.preventDefault();
      if (!loading && !uploadingAttachment && canSubmit && canSend) {
        speechInput.stop();
        onSubmit(event);
      }
    },
    [canSend, canSubmit, loading, onSubmit, speechInput, uploadingAttachment]
  );

  const toggleVoice = useCallback(() => {
    onVoiceError?.('');
    if (speechInput.listening) {
      speechInput.stop();
      return;
    }
    if (!speechInput.supported) {
      onVoiceError?.(t('chatVoiceUnsupported'));
      return;
    }
    dictationBaseRef.current = message.trim() ? `${message.trim()} ` : '';
    speechInput.start();
  }, [message, onVoiceError, speechInput, t]);

  const handleSubmit = useCallback(
    (event) => {
      event?.preventDefault?.();
      speechInput.stop();
      onSubmit(event);
    },
    [onSubmit, speechInput]
  );

  return (
    <form className="px-3 pb-3 pt-1" onSubmit={handleSubmit}>
      {hasAttachment ? (
        <div className="mb-2 flex flex-wrap gap-2 px-1">
          {attachments.map((attachment, index) => (
            <div key={attachment.url || index} className="relative">
              <img
                src={attachment.url}
                alt={t('chatAttachmentPreviewAlt')}
                className="h-14 w-14 rounded-xl border border-neutral-200 object-cover dark:border-neutral-800"
              />
              {attachment.purpose === 'design' ? (
                <span className="absolute inset-x-1 bottom-1 rounded bg-black/70 py-0.5 text-center text-[9px] font-medium text-white">
                  UI
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => onAttachmentRemove?.(index)}
                aria-label={t('removeAttachment')}
                className="absolute -end-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900/80 text-white transition hover:bg-rose-500"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {speechInput.listening ? (
        <>
          <input
            ref={voiceAttachRef}
            type="file"
            accept="image/*"
            className="sr-only"
            multiple
            onChange={(event) => {
              const files = listImageFiles(event.target.files);
              if (files.length) {
                onAttachmentSelect?.(files, 'attach');
              }
              event.target.value = '';
            }}
          />
          <SuggestionChips
            items={selectedSuggestions}
            onRemove={onRemoveSuggestion}
            removeLabel={t('removeSuggestion')}
          />
          <VoiceDictationBar
            listening={speechInput.listening}
            onStop={() => speechInput.stop()}
            onSend={() => handleSubmit()}
            onAdd={() => voiceAttachRef.current?.click()}
            canSend={canSubmit && canSend && !uploadingAttachment}
          />
        </>
      ) : (
        <div
          className={classNames(
            'overflow-visible rounded-xl border border-border bg-white shadow-sm dark:bg-panel-strong',
            canSend && 'focus-within:border-accent/40'
          )}
        >
          <QuoteBar text={quote?.text} onClear={onClearQuote} />
          <SuggestionChips
            items={selectedSuggestions}
            onRemove={onRemoveSuggestion}
            removeLabel={t('removeSuggestion')}
          />
          <label className="sr-only" htmlFor="chat-composer-input">
            {t('typeMessage')}
          </label>
          <textarea
            id="chat-composer-input"
            ref={textareaRef}
            value={message}
            onChange={(event) => onMessageChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={canSend ? t('typeMessage') : t('chatLoading')}
            disabled={!canSend}
            rows={3}
            className={classNames(
              'block min-h-[88px] w-full resize-none border-0 bg-transparent px-4 text-base leading-6 text-text outline-none placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-60 md:text-sm',
              hasSelectedSuggestions ? 'pt-2' : 'pt-3.5'
            )}
          />
          <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-1">
            <ImagePurposeAttachMenu
              disabled={!canSend || loading}
              busy={uploadingAttachment}
              onPick={onAttachmentSelect}
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={!canSend || loading}
                onClick={toggleVoice}
                title={t('chatVoiceInput')}
                aria-label={t('chatVoiceInput')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-200/70 hover:text-neutral-800 disabled:opacity-40 dark:hover:bg-accent-soft dark:hover:text-text"
              >
                <Mic size={16} />
              </button>
              {loading ? (
                <button
                  type="button"
                  onClick={onStop}
                  aria-label={t('stopRequest')}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-500 text-white transition hover:bg-rose-600"
                >
                  <Square size={12} fill="currentColor" strokeWidth={0} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!canSubmit || !canSend || uploadingAttachment}
                  aria-label={t('send')}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition enabled:hover:bg-neutral-800 disabled:opacity-35 dark:bg-white dark:text-neutral-950"
                >
                  <ArrowUp size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {voiceError ? <p className="mt-2 text-center text-xs text-rose-500">{voiceError}</p> : null}
    </form>
  );
};
